import { GameState } from '../core/GameState';
import { SaveManager } from '../core/SaveManager';
import { PromptSystem } from './PromptSystem';
import { ACHIEVEMENTS } from '../data/achievements';
import { TooltipSystem } from './TooltipSystem';
import { beautify, formatTime } from '../utils/format';
import { t } from '../i18n';

type MenuPage = 'none' | 'options' | 'stats' | 'info' | 'store';

interface MenuSystemOptions {
  onSaveToCloud?: () => Promise<boolean>;
  showToast?: (msg: string) => void;
}

export class MenuSystem {
  private currentPage: MenuPage = 'none';
  private container: HTMLElement | null = null;

  constructor(
    private saveManager: SaveManager,
    private promptSystem: PromptSystem,
    private tooltipSystem: TooltipSystem,
    private getState: () => GameState,
    private options: MenuSystemOptions = {},
  ) {}

  init(containerId: string): void {
    this.container = document.getElementById(containerId);
    this.injectMenuButtons();
  }

  private injectMenuButtons(): void {
    const header = document.getElementById('storeHeader');
    if (!header) return;

    const menuBar = document.createElement('div');
    menuBar.id = 'menuBar';
    const cloudSaveBlock = this.options.onSaveToCloud
      ? `
        <div class="menu-bar-cloud">
          <button class="menu-btn menu-btn-cloud" id="btn-save-cloud-bar">☁️ ${t('save_cloud')}</button>
          <span class="cloud-save-info" id="cloud-save-info" aria-label="${t('save_cloud_tooltip')}">ℹ️</span>
        </div>
      `
      : '';
    menuBar.innerHTML = `
      <button class="menu-btn" data-page="options">${t('options')}</button>
      <button class="menu-btn" data-page="stats">${t('stats')}</button>
      <button class="menu-btn" data-page="store">${t('store')}</button>
      ${cloudSaveBlock}
    `;
    header.parentElement?.appendChild(menuBar);

    menuBar.querySelectorAll<HTMLButtonElement>('.menu-btn[data-page]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const page = btn.dataset.page as MenuPage;
        if (page === 'store') {
          this.close();
        } else {
          this.toggle(page);
        }
      });
    });

    menuBar.querySelector('#btn-save-cloud-bar')?.addEventListener('click', () => {
      this.handleCloudSaveClick();
    });

    const infoEl = menuBar.querySelector('#cloud-save-info');
    if (infoEl && this.options.onSaveToCloud) {
      this.tooltipSystem.attach(infoEl as HTMLElement, () =>
        `<div style="max-width:240px">${t('save_cloud_tooltip')}</div>`);
    }
  }

  private async handleCloudSaveClick(): Promise<void> {
    const onSave = this.options.onSaveToCloud;
    if (!onSave) return;
    const ok = await onSave();
    const msg = ok ? t('save_cloud_done') : t('save_cloud_failed');
    this.options.showToast ? this.options.showToast(msg) : alert(msg);
  }

  toggle(page: MenuPage): void {
    if (this.currentPage === page) {
      this.close();
    } else {
      this.open(page);
    }
  }

  open(page: MenuPage): void {
    this.currentPage = page;
    if (!this.container) return;

    const state = this.getState();

    switch (page) {
      case 'options':
        this.container.innerHTML = this.renderOptions(state);
        this.attachOptionsHandlers(state);
        break;
      case 'stats':
        this.container.innerHTML = this.renderStats(state);
        this.attachStatsHandlers(state);
        break;
      case 'info':
        this.container.innerHTML = this.renderInfo();
        break;
      default:
        this.close();
        return;
    }

    this.container.classList.add('menu-open');
    this.attachCloseHandler();
  }

  close(): void {
    this.currentPage = 'none';
    if (this.container) {
      this.container.innerHTML = '';
      this.container.classList.remove('menu-open');
    }
  }

  private attachCloseHandler(): void {
    this.container?.querySelector('#btn-close-menu')?.addEventListener('click', () => {
      this.close();
    });
  }

  private renderOptions(state: GameState): string {
    const p = state.prefs;
    return `
      <div class="menu-content">
        <h2>${t('menu.options')}</h2>
        <div class="option-row">
          <label><input type="checkbox" data-pref="particles" ${p.particles ? 'checked' : ''} /> ${t('options.particles')}</label>
          <div class="option-desc">${t('options.particles.desc')}</div>
        </div>
        <div class="option-row">
          <label><input type="checkbox" data-pref="numbers" ${p.numbers ? 'checked' : ''} /> ${t('options.numbers')}</label>
          <div class="option-desc">${t('options.numbers.desc')}</div>
        </div>
        <div class="option-row">
          <label><input type="checkbox" data-pref="autosave" ${p.autosave ? 'checked' : ''} /> ${t('options.autosave')}</label>
          <div class="option-desc">${t('options.autosave.desc')}</div>
        </div>
        <div class="menu-actions">
          <button class="menu-action-btn" id="btn-save">${t('save')}</button>
          <button class="menu-action-btn danger" id="btn-wipe">${t('wipe')}</button>
        </div>
        <button class="menu-action-btn" id="btn-close-menu">${t('back')}</button>
      </div>
    `;
  }

  private attachOptionsHandlers(state: GameState): void {
    this.container?.querySelectorAll<HTMLInputElement>('[data-pref]').forEach((input) => {
      input.addEventListener('change', () => {
        const pref = input.dataset.pref as keyof typeof state.prefs;
        if (input.type === 'checkbox') {
          (state.prefs as any)[pref] = input.checked;
        } else if (input.type === 'range') {
          (state.prefs as any)[pref] = parseInt(input.value, 10);
        }
      });
    });

    this.container?.querySelector('#btn-save')?.addEventListener('click', () => {
      this.saveManager.save(state);
    });
    this.container?.querySelector('#btn-wipe')?.addEventListener('click', async () => {
      const ok = await this.promptSystem.confirm(t('confirm_wipe'), t('wipe'));
      if (ok) {
        this.saveManager.wipeSave();
        window.location.reload();
      }
    });
  }

  private renderStats(state: GameState): string {
    const totalBuildings = Array.from(state.buildings.values()).reduce((s, b) => s + b.amount, 0);
    const totalUpgrades = Array.from(state.upgrades.values()).filter((u) => u.bought).length;
    const timePlayed = Date.now() - state.startDate;

    const achievementItems = ACHIEVEMENTS
      .sort((a, b) => a.order - b.order)
      .map((def) => {
        const earned = state.achievements.has(def.id);
        const cls = earned ? 'achiev-item earned' : 'achiev-item locked';
        const icon = earned ? def.icon : '❓';
        return `<div class="${cls}" data-achiev="${def.id}">${icon}</div>`;
      })
      .join('');

    return `
      <div class="menu-content">
        <h2>${t('menu.stats')}</h2>
        <div class="stat-row">${t('stats.cookies_baked', { amount: beautify(state.cookiesEarned) })}</div>
        <div class="stat-row">${t('stats.cookies_current', { amount: beautify(state.cookies) })}</div>
        <div class="stat-row">${t('stats.cps', { cps: beautify(state.cookiesPs) })}</div>
        <div class="stat-row">${t('stats.clicks', { clicks: state.cookieClicks.toLocaleString() })}</div>
        <div class="stat-row">${t('stats.buildings_owned', { amount: totalBuildings })}</div>
        <div class="stat-row">${t('stats.upgrades_owned', { amount: totalUpgrades })}</div>
        <div class="stat-row">${t('stats.achievements_earned', { amount: state.achievements.size })} / ${ACHIEVEMENTS.length}</div>
        <div class="stat-row">${t('stats.time_played', { time: formatTime(timePlayed) })}</div>
        <div id="achievSection">
          <div id="achievHeader">
            <span>${t('achievements')} (${state.achievements.size}/${ACHIEVEMENTS.length})</span>
            <button class="menu-action-btn" id="btn-toggle-achiev">▸</button>
          </div>
          <div id="achievGrid" class="hidden">${achievementItems}</div>
        </div>
        <button class="menu-action-btn" id="btn-close-menu">${t('back')}</button>
      </div>
    `;
  }

  private attachStatsHandlers(state: GameState): void {
    const btn = this.container?.querySelector('#btn-toggle-achiev');
    const grid = this.container?.querySelector('#achievGrid');
    btn?.addEventListener('click', () => {
      const open = !grid?.classList.contains('hidden');
      grid?.classList.toggle('hidden', open);
      btn.textContent = open ? '▸' : '▾';
    });
    this.attachAchievTooltips(state);
  }

  private attachAchievTooltips(state: GameState): void {
    this.container?.querySelectorAll<HTMLElement>('[data-achiev]').forEach((el) => {
      const id = el.dataset.achiev!;
      const def = ACHIEVEMENTS.find((a) => a.id === id);
      if (!def) return;
      const earned = state.achievements.has(id);

      this.tooltipSystem.attach(el, () => {
        if (!earned) {
          return `<div style="min-width:160px;color:#888">???</div>`;
        }
        return `
          <div style="min-width:180px">
            <div style="font-size:15px;font-weight:700;margin-bottom:4px">${def.icon} ${def.name}</div>
            <div style="font-size:13px;color:#ccc">${def.desc}</div>
          </div>
        `;
      });
    });
  }

  private renderInfo(): string {
    return `
      <div class="menu-content">
        <h2>${t('info.title')}</h2>
        <p>${t('info.desc')}</p>
        <p>Based on Cookie Clicker by Orteil.</p>
        <p>Rewritten with TypeScript + Vite.</p>
        <button class="menu-action-btn" id="btn-close-menu">${t('back')}</button>
      </div>
    `;
  }
}
